/**
 * Sachi Retailer B2B Portal - JavaScript
 * Upload to Shopify: Admin > Content > Files
 */

// Configuration
const CONFIG = {
  SUPABASE_FUNCTION_URL: 'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/B2B-Generate-discount',
  UPDATE_DRAFT_URL: 'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/B2B-update-draft-order',
  SLACK_NOTIFY_URL: 'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/B2B-notify-slack',
  GET_LATEST_ORDER_URL: 'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/B2B-get-latest-order',
  SHOPIFY_CHECKOUT_URL: 'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/B2B-shopify-checkout',
  SUPABASE_ANON_KEY: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1cGV4ZGltc2Z1c3d3a291ZXdwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjExMzYwNzQsImV4cCI6MjA3NjcxMjA3NH0.O5ljAVpLj__2UWztWB0StyqsAh_96zzzmykK8goBMWc',
  // Filter to show only these categories
  ALLOWED_CATEGORIES: ['Beauty', 'Merchandise'],
  // Time limit for editing orders (in minutes)
  EDIT_TIME_LIMIT: 30
};

// Per-product order caps (limited stock). handle → max qty. Remove a line to uncap.
const PRODUCT_LIMITS = {
  'saponins-cream-cleanser': 5,
  'saffron-luminous-cleanser': 5,
};
function getProductLimit(handle) {
  return PRODUCT_LIMITS[handle] ?? Infinity; // ponytail: plain object lookup, no class needed
}

// ---------------------------------------------------------------------------
// MINIMUM ORDER + SHIPPING  — edit this table, nothing else.
//
// Keyed by ISO2 country code of the customer's shipping address; DEFAULT covers
// everywhere not listed. Set minPerSku or minTotal to 0 to switch that check OFF
// for that region (e.g. GB: { minPerSku: 0 } lets GB retailers order 1 of a SKU).
// A number in the customer metafield custom.b2b_minimum_qty_order replaces
// minTotal for that customer (minPerSku still applies).
//
// bands: [fromUnits, toUnits, priceGBP]
//   toUnits 0   = no upper bound
//   priceGBP null = "quote separately" (order still goes through at £0, flagged)
//
// Prices are GBP because draft orders and invoices are raised in GBP. What the
// retailer SEES in their own currency comes from SHIP_DISPLAY below.
// ---------------------------------------------------------------------------
const REGION_RULES = {
  GB:      { minPerSku: 5, minTotal: 20, shipLabel: 'Tracked Shipping (Royal Mail)', bands: [[20, 29, 35], [30, 39, 40], [40, 0, null]] },
  CA:      { minPerSku: 5, minTotal: 20, shipLabel: 'Tracked Shipping Canada',       bands: [[20, 29, 35], [30, 39, 40], [40, 0, null]] },
  US:      { minPerSku: 5, minTotal: 20, shipLabel: 'Tracked Shipping',              bands: [[20, 29, 35], [30, 39, 40], [40, 0, null]] },
  AU:      { minPerSku: 5, minTotal: 20, shipLabel: 'Tracked Shipping',              bands: [[20, 29, 35], [30, 39, 40], [40, 0, null]] },
  DEFAULT: { minPerSku: 5, minTotal: 20, shipLabel: 'Tracked Shipping',              bands: [[20, 29, 35], [30, 39, 40], [40, 0, null]] },
};

// Published shipping figures, one entry per band, in the retailer's own currency.
// Display only — the invoice always carries the GBP amount from bands above.
// A currency not listed here falls back to the live-converted GBP figure.
const SHIP_DISPLAY = {
  GBP: [35, 40],
  USD: [47, 55],
  AUD: [66, 75],
};

function getRegion() {
  const iso = (state.customerAddress?.country_code || '').toUpperCase();
  return REGION_RULES[iso] || REGION_RULES.DEFAULT;
}

// A limited-stock cap wins over the regional minimum — otherwise a SKU capped
// below the minimum would be impossible to order at all.
function getMinPerSku(handle) {
  return Math.min(getRegion().minPerSku || 0, getProductLimit(handle));
}

// units = paid units only (testers excluded). null below the first band.
function getShippingBand(units) {
  const r = getRegion();
  const i = r.bands.findIndex(([lo, hi]) => units >= lo && (hi === 0 || units <= hi));
  if (i === -1) return null;
  const gbp = r.bands[i][2];
  if (gbp === null) return { title: r.shipLabel + ' — quoted separately', gbp: 0, local: 0, quote: true };
  // SHIP_DISPLAY entries are positional: index i matches band i.
  const local = SHIP_DISPLAY[state.currency]?.[i] ?? gbp * (state.exchangeRate || 1);
  return { title: r.shipLabel, gbp, local, quote: false };
}

// Minimum-order check. Returns what is short, so the UI can say exactly what to fix.
function checkMOQ() {
  const r = getRegion();
  const lines = Object.values(state.cart);
  const units = lines.reduce((n, i) => n + i.quantity, 0);
  const shortSkus = lines
    .map(i => ({ title: i.product.title, have: i.quantity, need: getMinPerSku(i.product.handle) }))
    .filter(s => s.have < s.need);
  const minTotal = state.minTotalOverride ?? r.minTotal ?? 0;
  const shortBy = Math.max(0, minTotal - units);
  return { ok: shortSkus.length === 0 && shortBy === 0, shortBy, minTotal, minPerSku: r.minPerSku, shortSkus };
}

// Color name → hex map for variant swatches
const COLOR_MAP = {
  // Standard
  'black': '#1a1a1a', 'white': '#f2f0ed', 'red': '#d94040', 'blue': '#1c456b',
  'navy': '#0f2d4a', 'green': '#3d5a42', 'olive': '#6b7c45', 'pink': '#e3b6a6',
  'rose': '#c77d81', 'yellow': '#f0d060', 'orange': '#c07345', 'purple': '#6b4c7a',
  'lavender': '#b0a0cc', 'grey': '#9ca3af', 'gray': '#9ca3af', 'brown': '#78583a',
  'beige': '#e8d5b7', 'cream': '#f5efe3', 'gold': '#c9a227', 'silver': '#b8bec7',
  'teal': '#1d7a7a', 'coral': '#e06050', 'blush': '#e8b4a8', 'sage': '#8fa88a',
  'sand': '#c8b090', 'ivory': '#f5f0e8', 'nude': '#d4b09a', 'tan': '#c8956c',
  'cobalt': '#1a56a0', 'emerald': '#2d7a4a', 'lilac': '#c4a0d4', 'peach': '#e8a882',
  'mint': '#a0d4b8', 'taupe': '#a89080', 'charcoal': '#3d3d3d', 'blush pink': '#e8b4a8',
  // Sachi brand-specific
  'triphala pink': '#c8906c',
  'clarifying green': '#8fa68a',
  'ursolic blue': '#1d454f',
  'saponins blue': '#b8cde0',
  'earthy orange': '#c07345',
  'ursolic': '#1d454f',
  'triphala': '#c8906c',
  'clarifying': '#8fa68a',
};

function getSwatchColor(name) {
  if (!name) return null;
  const lower = name.toLowerCase().trim();
  if (COLOR_MAP[lower]) return COLOR_MAP[lower];
  for (const [key, val] of Object.entries(COLOR_MAP)) {
    if (lower.includes(key) || key.includes(lower)) return val;
  }
  return null;
}

function isColorOption(optionName) {
  if (!optionName) return false;
  return ['color', 'colour', 'finish', 'shade', 'tone'].includes(optionName.toLowerCase().trim());
}

// State
let state = {
  products: [],
  filteredProducts: [],
  cart: {},
  selectedVariants: {}, // productId -> variantIndex (for per-card swatch selection)
  testerItemsAdded: {}, // Track which tester items user has added to basket (opt-in): {productId: true/false}
  categories: new Set(),
  customerEmail: null,
  customerTags: [], // Customer tags from Shopify
  checkoutFlow: 'paylater', // 'shopify' or 'paylater'
  discountTiers: [], // Dynamically loaded from customer metafield (in USD)
  currency: 'USD',
  exchangeRate: 1.0, // Rate from store base currency (GBP) to active currency
  usdToLocalRate: 1.0, // Rate from USD to active currency (fetched for non-direct markets)
  customerAddress: null,
  minTotalOverride: null, // From customer metafield custom.b2b_minimum_qty_order
  lastOrder: null, // Store last order data for PDF generation
  isAmendingOrder: false, // Flag when editing an existing order
  amendingOrderRef: null, // Reference to original order being amended
  orderSubmittedAt: null, // Timestamp when order was submitted
  editCountdownInterval: null // Interval ID for countdown timer
};

// Format money using Shopify's currency and browser's Intl API
function formatMoney(amount) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: state.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (e) {
    // Fallback if currency not supported
    console.warn('Currency formatting failed for', state.currency, e);
    return `${state.currency} ${amount.toFixed(2)}`;
  }
}

// Check if there's a recent order and show the "Last Order" button
async function checkAndShowLastOrderButton() {
  console.log('🔍 checkAndShowLastOrderButton called');
  
  // Always check Shopify for latest draft order
  console.log('📡 Checking Shopify for recent draft orders...');
  const draftOrder = await fetchLatestDraftOrder();
  
  console.log('📦 Draft order response:', draftOrder);
  
  if (draftOrder) {
    const orderDate = new Date(draftOrder.created_at);
    const now = new Date();
    const daysAgo = (now - orderDate) / (1000 * 60 * 60 * 24);
    
    console.log('📅 Draft order date:', orderDate, 'Days ago:', daysAgo);
    
    if (daysAgo <= 150) {
      const orderName = draftOrder.name;
      console.log('✅ Found recent draft order:', orderName);
      
      const btn = document.getElementById('lastOrderBtn');
      
      if (btn) {
        btn.style.display = 'block';
        console.log('✅ Showing last order button');
      }
    } else {
      console.log('⚠️ Draft order too old:', daysAgo, 'days');
      console.log('ℹ️ No recent orders to display');
    }
  } else {
    console.log('⚠️ No draft order returned');
    console.log('ℹ️ No recent orders to display');
  }
}

// Fetch latest draft order from Shopify via Supabase function
async function fetchLatestDraftOrder() {
  try {
    const customerData = document.querySelector('[data-customer-email]');
    if (!customerData || !customerData.dataset.customerId) {
      return null;
    }
    
    console.log('📡 Calling Supabase function to fetch latest order...');
    
    // Call Supabase function to fetch from Shopify
    const response = await fetch(CONFIG.GET_LATEST_ORDER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': CONFIG.SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        customer_id: parseInt(customerData.dataset.customerId)
      })
    });
    
    if (!response.ok) {
      console.warn('⚠️ Could not fetch latest order from Supabase');
      return null;
    }
    
    const data = await response.json();
    
    if (data.success && data.order) {
      console.log('✅ Fetched latest order:', data.order.name);
      return data.order;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error fetching latest order:', error);
    return null;
  }
}

// View last order details
async function viewLastOrder() {
  try {
    // Always fetch from Shopify
    console.log('📡 Fetching latest order from Shopify...');
    const draftOrder = await fetchLatestDraftOrder();
    
    console.log('📦 Draft order data:', draftOrder);
    
    if (!draftOrder) {
      alert('No recent orders found. Please place an order first.');
      return;
    }
    
    if (!draftOrder.line_items || draftOrder.line_items.length === 0) {
      console.error('❌ Draft order has no line items:', draftOrder);
      alert('This order has no items. Please contact support.');
      return;
    }
    
    console.log('📋 Processing', draftOrder.line_items.length, 'line items...');
    
    // Match products from our state to recalculate in current currency
    const items = draftOrder.line_items.map(item => {
      console.log('🔍 Processing item:', item.title, 'Product ID:', item.product_id);
      
      // Try to find matching product in our products list
      const matchedProduct = state.products.find(p => 
        p.id === item.product_id || p.title === item.title
      );
      
      if (matchedProduct) {
        console.log('✅ Matched product:', matchedProduct.title);
      } else {
        console.log('⚠️ No match found, using draft order data');
      }
      
      return {
        product: matchedProduct || {
          id: item.product_id,
          title: item.title,
          rrpPrice: parseFloat(item.price) * state.exchangeRate, // Convert to current currency
          size: item.variant_title !== 'Default Title' ? item.variant_title : '',
          sku: item.sku || ''
        },
        quantity: item.quantity
      };
    });
  
  // Recalculate totals in current currency
  const subtotal = items.reduce((sum, item) => sum + (item.product.rrpPrice * item.quantity), 0);
  
  // Recalculate discount based on current pricing tiers
  const localTiers = convertTiersToLocalCurrency(state.discountTiers);
  let discount = null;
  const applicableTier = localTiers.find(tier => 
    subtotal >= tier.minAmount && subtotal < tier.maxAmount
  );
  
  if (applicableTier) {
    const discountAmount = subtotal * (applicableTier.discountPercent / 100);
    discount = {
      percent: applicableTier.discountPercent,
      amount: discountAmount,
      label: `${applicableTier.discountPercent}% off`
    };
  }
  
  const total = discount ? subtotal - discount.amount : subtotal;
  
  // Get customer name - try multiple sources
  let customerName = 'Customer';
  
  // Try from draft order/order customer object
  if (draftOrder.customer) {
    if (draftOrder.customer.first_name && draftOrder.customer.last_name) {
      customerName = `${draftOrder.customer.first_name} ${draftOrder.customer.last_name}`;
    } else if (draftOrder.customer.name) {
      customerName = draftOrder.customer.name;
    }
  }
  
  // Fallback to DOM data if still not set
  if (customerName === 'Customer') {
    const customerData = document.querySelector('[data-customer-name]');
    if (customerData && customerData.dataset.customerName) {
      customerName = customerData.dataset.customerName;
    }
  }
  
  console.log('👤 Customer name:', customerName);
  
  // Detect if this is a draft order or real order
  // Draft orders have "D" in the name (e.g., #D1807)
  const isDraftOrder = draftOrder.name.includes('D');
  console.log('📋 Order type:', isDraftOrder ? 'Draft Order' : 'Real Order');
  
  // Extract numeric draft order ID
  let draftOrderId = draftOrder.id;
  if (typeof draftOrderId === 'string' && draftOrderId.includes('gid://')) {
    draftOrderId = draftOrderId.split('/').pop();
  }
  console.log('🔑 Draft Order ID:', draftOrderId);
  
  // Restore state.lastOrder for the confirmation page to work
  state.lastOrder = {
    orderNumber: draftOrder.name.replace('#', ''),
    orderName: draftOrder.name,
    draftOrderId: draftOrderId, // Use actual numeric ID
    customerName: customerName,
    items: items,
    subtotal: subtotal,
    discount: discount,
    total: total,
    isFromShopify: true,
    isDraftOrder: isDraftOrder
  };
  
  // Also restore submission timestamp if available
  if (draftOrder.created_at) {
    state.orderSubmittedAt = draftOrder.created_at;
  }
  
  // Create a mock order object for showConfirmationPage
  const mockOrder = {
    name: draftOrder.name,
    id: draftOrder.name.replace('#', '')
  };
  
  const totals = {
    subtotal: subtotal,
    discount: discount,
    total: total,
    items: items
  };
  
  // Show the confirmation page with stored data
    console.log('📄 Showing confirmation page...');
    showConfirmationPage(mockOrder, state.lastOrder.customerName, totals);
  } catch (error) {
    console.error('❌ Error in viewLastOrder:', error);
    alert('Error loading order details: ' + error.message);
  }
}

// Check if order can still be edited (within time limit)
function canEditOrder() {
  if (!state.lastOrder || !state.orderSubmittedAt) {
    return false;
  }
  
  const now = new Date();
  const submittedAt = new Date(state.orderSubmittedAt);
  const minutesElapsed = (now - submittedAt) / 1000 / 60;
  
  return minutesElapsed < CONFIG.EDIT_TIME_LIMIT;
}

// Get remaining edit time in seconds
function getRemainingEditTime() {
  if (!state.orderSubmittedAt) return 0;
  
  const now = new Date();
  const submittedAt = new Date(state.orderSubmittedAt);
  const secondsElapsed = (now - submittedAt) / 1000;
  const totalSeconds = CONFIG.EDIT_TIME_LIMIT * 60;
  const remaining = Math.max(0, totalSeconds - secondsElapsed);
  
  return Math.floor(remaining);
}

// Format seconds to MM:SS
function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Start countdown timer for edit window
function startEditCountdown() {
  // Clear any existing interval
  if (state.editCountdownInterval) {
    clearInterval(state.editCountdownInterval);
  }
  
  const updateCountdown = () => {
    const remaining = getRemainingEditTime();
    const timerElement = document.getElementById('editTimerCountdown');
    const timerContainer = document.getElementById('editTimerContainer');
    const editBtn = document.getElementById('editOrderBtn');
    const reorderBtn = document.getElementById('reorderBtn');
    
    if (remaining <= 0) {
      // Time expired - hide timer container and both buttons
      if (timerContainer) {
        timerContainer.style.display = 'none';
      }
      if (editBtn) {
        editBtn.style.display = 'none';
      }
      if (reorderBtn) {
        reorderBtn.style.display = 'none';
      }
      if (state.editCountdownInterval) {
        clearInterval(state.editCountdownInterval);
      }
    } else {
      // Update timer display
      if (timerElement) {
        timerElement.textContent = formatTime(remaining);
        // Change color when less than 5 minutes remaining
        if (remaining < 300) {
          timerElement.style.color = '#f59e0b';
        }
        if (remaining < 60) {
          timerElement.style.color = '#ef4444';
        }
      }
    }
  };
  
  // Update immediately
  updateCountdown();
  
  // Then update every second
  state.editCountdownInterval = setInterval(updateCountdown, 1000);
}

function editLastOrder() {
  if (!state.lastOrder) {
    console.warn('⚠️ No order to edit');
    return;
  }
  
  // Check if editing is still allowed
  if (!canEditOrder()) {
    alert(`The editing window for this order has expired.\n\nOrders can only be edited within ${CONFIG.EDIT_TIME_LIMIT} minutes of submission.\n\nPlease place a new order instead.`);
    return;
  }
  
  // Set amendment flags
  state.isAmendingOrder = true;
  state.amendingOrderRef = state.lastOrder.orderName || `#${state.lastOrder.orderNumber}`;
  
  console.log('✏️ Editing order:', state.amendingOrderRef);
  
  // Restore cart from last order
  state.cart = {};
  state.lastOrder.items.forEach(item => {
    state.cart[item.product.id] = {
      product: item.product,
      quantity: item.quantity
    };
  });
  
  // Return to main page
  document.getElementById('confirmationPage').style.display = 'none';
  document.querySelector('.main-content').style.display = 'block';
  document.querySelector('header').style.display = 'block';
  
  // Update UI
  updateOrderSummary();
  renderProducts();
  
  // Scroll to top
  window.scrollTo(0, 0);
}

// Reorder - load items into cart and go to products page
function reorderLastOrder() {
  if (!state.lastOrder) {
    console.warn('⚠️ No order to reorder');
    return;
  }
  
  console.log('🔄 Reordering:', state.lastOrder.orderName);
  
  // Load items into cart
  state.cart = {};
  state.lastOrder.items.forEach(item => {
    state.cart[item.product.id] = {
      product: item.product,
      quantity: item.quantity
    };
  });
  
  // Return to main page
  document.getElementById('confirmationPage').style.display = 'none';
  document.querySelector('.main-content').style.display = 'block';
  document.querySelector('header').style.display = 'block';
  
  // Update UI
  updateOrderSummary();
  renderProducts();
  
  // Scroll to top
  window.scrollTo(0, 0);
}

function showAmendmentNotice() {
  const notice = document.createElement('div');
  notice.style.cssText = `
    position: fixed;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    background: #f59e0b;
    color: white;
    padding: 1rem 2rem;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    z-index: 9999;
    font-weight: 500;
    animation: slideDown 0.3s ease-out;
  `;
  notice.innerHTML = `
    <div style="display: flex; align-items: center; gap: 0.5rem;">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
      </svg>
      <span>Editing Order ${state.amendingOrderRef} - Changes will create an amendment</span>
      <button onclick="this.parentElement.parentElement.remove()" style="background: none; border: none; color: white; cursor: pointer; margin-left: 1rem; font-size: 1.2rem;">&times;</button>
    </div>
  `;
  
  document.body.appendChild(notice);
  
  // Add CSS animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideDown {
      from { transform: translate(-50%, -100%); opacity: 0; }
      to { transform: translate(-50%, 0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (notice.parentElement) {
      notice.style.animation = 'slideDown 0.3s ease-out reverse';
      setTimeout(() => notice.remove(), 300);
    }
  }, 10000);
}

// Send Slack notification for order amendments
async function sendAmendmentNotification(order, customerDataset, discount, originalOrderRef, originalItems, originalSubtotal, originalTotal, originalDiscount) {
  try {
    console.log('📨 Sending amendment notification to Slack...');
    
    // Extract numeric ID from GraphQL GID if needed
    let numericId = order.id;
    if (typeof order.id === 'string' && order.id.includes('gid://')) {
      numericId = order.id.split('/').pop();
    }
    
    const orderData = {
      invoiceNumber: order.name.replace('#', ''),
      originalOrderRef: originalOrderRef,
      isAmendment: true,
      amendmentType: 'RETAILER_AMENDED', // Flag for Slack formatting
      subtotal: state.lastOrder.subtotal,
      total: state.lastOrder.total,
      currency: state.currency,
      shippingCost: 0,
      duties: 0,
      taxes: 0,
      draftOrderUrl: `https://admin.shopify.com/store/sachi-skin/draft_orders/${numericId}`,
      logoUrl: 'https://cdn.shopify.com/s/files/1/0236/1258/8078/files/logo-min.png?v=1629423611'
    };
    
    const customerData = {
      name: customerDataset.dataset.customerName,
      email: state.customerEmail,
      company: state.customerAddress?.company || '',
      shippingAddress: state.customerAddress,
      billingAddress: state.customerAddress
    };
    
    // Current (amended) items
    const cartItems = state.lastOrder.items.map(item => ({
      title: item.product.title,
      size: item.product.size || 'Standard',
      quantity: item.quantity,
      price: item.product.rrpPrice,
      total: item.product.rrpPrice * item.quantity,
      sku: item.product.sku,
      hsCode: item.product.hsCode,
      category: item.product.category,
      countryOfOrigin: item.product.countryOfOrigin
    }));
    
    // Original items (for comparison)
    const originalItemsFormatted = originalItems.map(item => ({
      title: item.product.title,
      size: item.product.size || 'Standard',
      quantity: item.quantity,
      price: item.product.rrpPrice,
      total: item.product.rrpPrice * item.quantity,
      sku: item.product.sku
    }));
    
    const discountInfo = {
      amount: discount ? discount.amount : 0,
      percentage: discount ? discount.percent : 0
    };
    
    const response = await fetch(CONFIG.SLACK_NOTIFY_URL, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'apikey': CONFIG.SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        orderData,
        customerData,
        cartItems, // Amended items
        originalItems: originalItemsFormatted, // Original items for comparison
        discountInfo,
        originalSubtotal: originalSubtotal,
        originalTotal: originalTotal,
        originalDiscount: originalDiscount
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send amendment notification');
    }
    
    const result = await response.json();
    console.log('✅ Amendment notification sent to Slack:', result);
    
  } catch (error) {
    console.error('❌ Amendment notification error:', error);
    throw error;
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', async () => {
  // Check if user just logged in and needs to be redirected
  const loginRedirect = sessionStorage.getItem('login_redirect');
  if (loginRedirect && loginRedirect !== window.location.pathname) {
    sessionStorage.removeItem('login_redirect');
    window.location.href = loginRedirect;
    return;
  }
  
  // Detect currency from Shopify
  await detectCurrency();
  
  const customerData = document.querySelector('[data-customer-email]');
  if (customerData) {
    state.customerEmail = customerData.dataset.customerEmail;
    
    // Load customer tags and determine checkout flow
    if (customerData.dataset.customerTags) {
      state.customerTags = customerData.dataset.customerTags.split(',').map(tag => tag.trim());
      console.log('🏷️ Customer tags:', state.customerTags);
      
      // Determine checkout flow based on tags
      // B2B_paylater takes precedence over B2B
      if (state.customerTags.includes('B2B_paylater')) {
        state.checkoutFlow = 'paylater';
        console.log('💳 Checkout flow: Pay Later (Draft Order)');
      } else if (state.customerTags.includes('B2B')) {
        state.checkoutFlow = 'shopify';
        console.log('💳 Checkout flow: Shopify Checkout (Discount Code)');
      } else {
        state.checkoutFlow = 'paylater'; // Default fallback
        console.log('💳 Checkout flow: Pay Later (Default)');
      }
    }
    
    // Load customer address
    if (customerData.dataset.customerAddress) {
      try {
        state.customerAddress = JSON.parse(customerData.dataset.customerAddress);
        console.log('📍 Customer address loaded:', state.customerAddress);
      } catch (e) {
        console.warn('⚠️ Failed to parse customer address:', e);
      }
    }
    
    // Per-customer minimum order units (overrides the regional minTotal)
    const minQty = parseInt(customerData.dataset.customerMinQty, 10);
    if (Number.isInteger(minQty) && minQty >= 0) state.minTotalOverride = minQty;

    // Load customer's B2B pricing tiers from metafield (values are in USD)
    const b2bPricing = customerData.dataset.customerB2bPricing;
    console.log('🔍 Debug - Raw metafield value:', b2bPricing);
    console.log('🔍 Debug - Metafield exists:', customerData.dataset.debugHasMetafield);
    console.log('🔍 Debug - Metafield type:', customerData.dataset.debugMetafieldType);
    console.log('🔍 Debug - All dataset:', customerData.dataset);
    
    if (b2bPricing && b2bPricing !== '' && b2bPricing !== 'undefined') {
      state.discountTiers = parseB2BPricing(b2bPricing);
      console.log('✅ Loaded customer B2B tiers (USD):', state.discountTiers);
      console.log('💱 Currency:', state.currency, '| Exchange Rate:', state.exchangeRate);
      console.log('💰 Converted tiers:', convertTiersToLocalCurrency(state.discountTiers));
    } else {
      console.warn('⚠️ Customer has no custom.b2b_price metafield. Using fallback tiers.');
      console.log('💡 To fix: Add custom.b2b_price metafield to customer in Shopify Admin');
      console.log('💡 Or ensure Storefront API access is enabled for the metafield definition');
      // Fallback default tiers (in GBP)
      state.discountTiers = [
        { minAmount: 0, maxAmount: 1000, discountPercent: 40 },
        { minAmount: 1000, maxAmount: 5000, discountPercent: 50 },
        { minAmount: 5000, maxAmount: Infinity, discountPercent: 60 }
      ];
    }
  }
  
  // Check and show last order button if there's a recent order
  checkAndShowLastOrderButton();

  // If the customer's shipping country isn't already in the URL, redirect with
  // ?country=XX so the page initialises fresh with the correct currency rates.
  const urlCountryParam = new URLSearchParams(window.location.search).get('country');
  if (!urlCountryParam && state.customerAddress?.country_code) {
    const url = new URL(window.location.href);
    url.searchParams.set('country', state.customerAddress.country_code.toLowerCase());
    window.location.replace(url.toString());
    return; // stop here — page will reload with the param
  }

  await loadProducts();
  setupEventListeners();
  renderProducts();
  updateOrderSummary();
});

// Map ISO 3166-1 alpha-2 country codes → ISO 4217 currency codes.
// Used to auto-switch prices when a customer's shipping address is in a
// different country to the Shopify market they landed on.
const COUNTRY_TO_CURRENCY = {
  // British Isles
  GB: 'GBP', IM: 'GBP', JE: 'GBP', GG: 'GBP',
  // Americas
  US: 'USD', CA: 'CAD', MX: 'MXN', BR: 'BRL', AR: 'ARS',
  // Asia-Pacific
  AU: 'AUD', NZ: 'NZD', SG: 'SGD', HK: 'HKD', JP: 'JPY',
  CN: 'CNY', IN: 'INR', KR: 'KRW', TH: 'THB', MY: 'MYR',
  ID: 'IDR', PH: 'PHP', TW: 'TWD', PK: 'PKR', BD: 'BDT',
  // Middle East / Africa
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', ZA: 'ZAR',
  EG: 'EGP', NG: 'NGN', KE: 'KES',
  // Europe (non-EUR)
  CH: 'CHF', NO: 'NOK', SE: 'SEK', DK: 'DKK', PL: 'PLN',
  CZ: 'CZK', HU: 'HUF', RO: 'RON', RS: 'RSD', UA: 'UAH',
  // Eurozone
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
  BE: 'EUR', AT: 'EUR', PT: 'EUR', FI: 'EUR', IE: 'EUR',
  GR: 'EUR', LU: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR',
  LV: 'EUR', LT: 'EUR', HR: 'EUR', CY: 'EUR', MT: 'EUR',
};

/**
 * Switch currency state to the correct currency for a given ISO2 country code.
 * Fetches live GBP→currency and USD→currency rates from Frankfurter.
 * No-ops if the country maps to the same currency already active.
 */
async function applyCurrencyForCountry(iso2) {
  if (!iso2) return;
  const currency = COUNTRY_TO_CURRENCY[iso2.toUpperCase()];
  if (!currency || currency === state.currency) return;

  console.log(`🌍 Switching currency: ${state.currency} → ${currency} (country: ${iso2})`);
  state.currency = currency;

  if (currency === 'GBP') {
    state.exchangeRate = 1.0;
    // Still need USD→GBP for tier threshold conversion
    try {
      const res = await fetch('https://api.frankfurter.app/latest?from=USD&to=GBP');
      if (res.ok) {
        const data = await res.json();
        state.usdToLocalRate = data.rates['GBP'] || 1.0;
      }
    } catch (e) { state.usdToLocalRate = 1.0; }
  } else {
    // Fetch GBP → currency (used to display product prices)
    try {
      const res = await fetch(`https://api.frankfurter.app/latest?from=GBP&to=${currency}`);
      if (res.ok) {
        const data = await res.json();
        state.exchangeRate = data.rates[currency] || 1.0;
        console.log(`GBP → ${currency}: ${state.exchangeRate}`);
      }
    } catch (e) {
      console.warn('Frankfurter GBP rate fetch failed:', e);
    }
    // Fetch USD → currency (used for volume discount tier thresholds)
    const directMarkets = ['USD', 'GBP', 'EUR'];
    if (directMarkets.includes(currency)) {
      state.usdToLocalRate = (currency === 'USD') ? 1.0 : state.exchangeRate;
    } else {
      try {
        const res = await fetch(`https://api.frankfurter.app/latest?from=USD&to=${currency}`);
        if (res.ok) {
          const data = await res.json();
          state.usdToLocalRate = data.rates[currency] || 1.0;
          console.log(`USD → ${currency}: ${state.usdToLocalRate}`);
        }
      } catch (e) {
        console.warn('Frankfurter USD rate fetch failed:', e);
        state.usdToLocalRate = state.exchangeRate;
      }
    }
  }

  console.log(`✅ Currency set: ${state.currency} | GBP rate: ${state.exchangeRate} | USD rate: ${state.usdToLocalRate}`);
}

// Detect currency and exchange rate from Shopify
// Shopify.currency.rate is always STORE_BASE → ACTIVE (GBP → active), not USD → active.
// For GBP/EUR/USD markets the tier thresholds are used as-is.
// For all other markets we fetch the USD → active rate from Frankfurter (free, no key).
// Supports ?country=GB (ISO2) URL param to force a specific country's currency.
async function detectCurrency() {
  // --- URL param override: ?country=GB forces GBP regardless of Shopify market ---
  const urlCountry = new URLSearchParams(window.location.search).get('country');
  if (urlCountry) {
    // Seed defaults from Shopify first so exchangeRate has a sane starting value
    if (typeof Shopify !== 'undefined' && Shopify.currency) {
      state.currency = Shopify.currency.active || 'GBP';
      state.exchangeRate = parseFloat(Shopify.currency.rate) || 1.0;
    }
    await applyCurrencyForCountry(urlCountry);
    console.log('🌐 Currency forced via ?country=' + urlCountry + ':', state.currency);
    console.log('Sample format:', formatMoney(1234.56));
    return;
  }

  if (typeof Shopify !== 'undefined' && Shopify.currency) {
    state.currency = Shopify.currency.active || 'USD';
    state.exchangeRate = parseFloat(Shopify.currency.rate) || 1.0;
  } else {
    console.warn('Currency not found in Shopify object, using USD defaults');
  }

  const directMarkets = ['USD', 'GBP', 'EUR'];
  if (directMarkets.includes(state.currency)) {
    // Thresholds stored in profile are for this currency — use as-is
    state.usdToLocalRate = 1.0;
  } else {
    // Fetch USD → active currency rate from Frankfurter
    try {
      const res = await fetch(`https://api.frankfurter.app/latest?from=USD&to=${state.currency}`);
      if (res.ok) {
        const data = await res.json();
        state.usdToLocalRate = data.rates[state.currency] || 1.0;
        console.log('USD → ' + state.currency + ' rate (Frankfurter):', state.usdToLocalRate);
      } else {
        console.warn('Frankfurter rate fetch failed, falling back to GBP exchange rate');
        state.usdToLocalRate = state.exchangeRate;
      }
    } catch (e) {
      console.warn('Frankfurter unavailable, falling back to GBP exchange rate:', e);
      state.usdToLocalRate = state.exchangeRate;
    }
  }

  console.log('Currency:', state.currency, '| GBP rate:', state.exchangeRate, '| USD rate:', state.usdToLocalRate);
  console.log('Sample format:', formatMoney(1234.56));
}

// Convert tier display amounts to local currency.
// Tier thresholds in the customer metafield are defined in GBP.
// GBP/EUR/USD: use as-is. All other markets: multiply by exchangeRate (GBP → local).
function convertTiersToLocalCurrency(tiersGBP) {
  const isDirectMarket = ['USD', 'GBP', 'EUR'].includes(state.currency);
  const rate = isDirectMarket ? 1.0 : state.exchangeRate;
  return tiersGBP.map(tier => ({
    ...tier,
    minAmount: tier.minAmount * rate,
    maxAmount: tier.maxAmount === Infinity ? Infinity : tier.maxAmount * rate,
    originalMinUSD: tier.minAmount,
    originalMaxUSD: tier.maxAmount
  }));
}

// Load products from Shopify
async function loadProducts() {
  try {
    let allProducts = [];
    let page = 1;
    while (true) {
      const res = await fetch(`/products.json?limit=250&page=${page}`);
      const data = await res.json();
      allProducts = allProducts.concat(data.products);
      if (data.products.length < 250) break;
      page++;
    }

    const data = { products: allProducts };
    
    console.log('📊 Total products loaded:', data.products.length);
    
    // Fetch "Individual Items" collection to prioritize those products
    let individualItemsIds = new Set();
    try {
      const collectionResponse = await fetch('/collections/individual-items/products.json?limit=250');
      if (collectionResponse.ok) {
        const collectionData = await collectionResponse.json();
        individualItemsIds = new Set(collectionData.products.map(p => p.id));
        console.log('✨ Individual Items collection loaded:', individualItemsIds.size, 'products');
      }
    } catch (e) {
      console.log('ℹ️ Individual Items collection not found or error loading:', e.message);
    }
    
    // Debug: Log all unique product types
    const allTypes = [...new Set(data.products.map(p => p.product_type || 'Uncategorized'))];
    console.log('📋 All product types found:', allTypes);
    
    // Debug: Log ALL products with their types
    console.log('🔍 ALL PRODUCTS:', data.products.map(p => ({ 
      title: p.title, 
      type: p.product_type || 'Uncategorized' 
    })));
    
    state.products = data.products
      .filter(product => {
        // The b2b tag is the sole gate — any product tagged b2b appears in the portal
        // regardless of product_type, visibility in collections, or search indexing.
        // /products.json returns tags as a comma-separated string, not an array.
        const tagList = Array.isArray(product.tags)
          ? product.tags
          : (product.tags || '').split(',').map(t => t.trim());
        const hasB2BTag = tagList.some(tag => tag.toLowerCase() === 'b2b');
        if (!hasB2BTag) {
          console.log('❌ Filtered out (no B2B tag):', product.title, '| tags:', product.tags);
          return false;
        }
        
        return true;
      })
      .map(product => {
        const category = product.product_type || 'Uncategorized';
        state.categories.add(category);

        const productImage = product.images[0]?.src || '';

        // Build variants array (each entry used when adding to cart)
        // For Beauty products: only expose the first variant (e.g. 30ml, never 60ml or
        // any size that carries a different/discounted price tier)
        const sourceVariants = category === 'Beauty'
          ? product.variants.slice(0, 1)
          : product.variants;
        const variants = sourceVariants.map(v => ({
          id: String(v.id),
          title: v.title !== 'Default Title' ? v.title : '',
          price: parseFloat(v.price),
          image: v.featured_image?.src || productImage,
          sku: v.sku || '',
          hsCode: v.harmonized_system_code || '3304990000',
          countryOfOrigin: product.country_code_of_origin || 'AU',
        }));

        // Detect if first option is color-type (e.g. "Color", "Colour")
        // product.options from /products.json are objects: {id, name, values, ...}
        const firstOption = (product.options || [])[0];
        const firstOptionName = (typeof firstOption === 'object' && firstOption !== null)
          ? (firstOption.name || '')
          : (firstOption || '');
        const variantsAreColors = isColorOption(firstOptionName) ||
          (product.variants.length > 1 && product.variants.every(v => getSwatchColor(v.title) !== null));

        return {
          id: String(product.id),      // Shopify product ID — card key
          productId: String(product.id),
          title: product.title,
          variants,
          variantsAreColors,
          description: product.body_html?.replace(/<[^>]*>/g, '').substring(0, 100) || '',
          shortDescription: product.body_html?.replace(/<[^>]*>/g, '').substring(0, 60) || '',
          handle: product.handle,
          image: productImage,
          rrpPrice: variants[0]?.price || 0,
          category,
          isIndividualItem: individualItemsIds.has(product.id),
        };
      })
      .sort((a, b) => {
        // Sort: Individual Items first, then others
        if (a.isIndividualItem && !b.isIndividualItem) return -1;
        if (!a.isIndividualItem && b.isIndividualItem) return 1;
        return 0; // Keep original order within each group
      });
    
    state.filteredProducts = [...state.products];
    
    console.log('✅ Products after filter:', state.products.length);
    console.log('📦 Products by category:', 
      state.products.reduce((acc, p) => {
        acc[p.category] = (acc[p.category] || 0) + 1;
        return acc;
      }, {})
    );
    
    // Populate category filter
    const categoryFilter = document.getElementById('categoryFilter');
    // Clear existing options except "All Categories"
    while (categoryFilter.options.length > 1) {
      categoryFilter.remove(1);
    }
    // Add category options
    state.categories.forEach(cat => {
      const option = document.createElement('option');
      option.value = cat.toLowerCase();
      option.textContent = cat;
      categoryFilter.appendChild(option);
    });
    
  } catch (error) {
    console.error('Error loading products:', error);
  }
}

// Parse B2B pricing from metafield
// Formats supported:
// 1. Simple flat rate: "50%" = 50% off everything
// 2. Tiered pricing (newline): "40% < $1,000\n50% > $1,000\n60% > $5,000"
// 3. Tiered pricing (space): "40% < $1,000 50% > $1,000 55% > $25,000 60% > $50,000"
function parseB2BPricing(value) {
  if (!value || value.trim() === '') return [];
  
  // Check if it's a simple flat rate (just a percentage with no threshold)
  const flatRateMatch = value.trim().match(/^(\d+)%$/);
  if (flatRateMatch) {
    const percent = parseInt(flatRateMatch[1]);
    console.log(`💰 Flat rate pricing: ${percent}% off all orders`);
    return [{ minAmount: 0, maxAmount: Infinity, discountPercent: percent }];
  }
  
  // Parse tiered pricing with thresholds
  // Split by newlines OR by pattern matching (to handle space-separated format)
  const tiers = [];
  const lines = value.includes('\n') 
    ? value.split('\n').filter(line => line.trim())
    : value.match(/\d+%\s*[<>]\s*\$?[\d,]+/g) || [];
  
  lines.forEach((line, index) => {
    const match = line.match(/(\d+)%\s*([<>])\s*\$?([\d,]+)/);
    if (match) {
      const percent = parseInt(match[1]);
      const operator = match[2];
      const amount = parseFloat(match[3].replace(/,/g, ''));
      
      if (operator === '<') {
        // Discount for amounts less than threshold
        tiers.push({ minAmount: 0, maxAmount: amount, discountPercent: percent });
      } else if (operator === '>') {
        // Discount for amounts greater than threshold
        const nextLine = lines[index + 1];
        let maxAmount = Infinity;
        
        if (nextLine) {
          const nextMatch = nextLine.match(/\d+%\s*>\s*\$?([\d,]+)/);
          if (nextMatch) {
            maxAmount = parseFloat(nextMatch[1].replace(/,/g, ''));
          }
        }
        
        tiers.push({ minAmount: amount, maxAmount: maxAmount, discountPercent: percent });
      }
    }
  });
  
  const sortedTiers = tiers.sort((a, b) => a.minAmount - b.minAmount);
  console.log(`📊 Tiered pricing loaded:`, sortedTiers);
  return sortedTiers;
}

// Event listeners
function setupEventListeners() {
  document.getElementById('searchInput').addEventListener('input', filterProducts);
  document.getElementById('categoryFilter').addEventListener('change', filterProducts);
}

// Filter products
function filterProducts() {
  const searchQuery = document.getElementById('searchInput').value.toLowerCase();
  const selectedCategory = document.getElementById('categoryFilter').value;
  
  state.filteredProducts = state.products.filter(product => {
    const matchesSearch = product.title.toLowerCase().includes(searchQuery) || 
                         product.description.toLowerCase().includes(searchQuery);
    const matchesCategory = selectedCategory === 'all' || 
                           product.category.toLowerCase() === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  renderProducts();
}

// Render products
function renderProducts() {
  const grid = document.getElementById('productsGrid');
  const noResults = document.getElementById('noResults');
  
  if (state.filteredProducts.length === 0) {
    grid.innerHTML = '';
    noResults.style.display = 'block';
    return;
  }
  
  noResults.style.display = 'none';
  
  grid.innerHTML = state.filteredProducts.map((product, index) => {
    const selectedIdx = state.selectedVariants[product.id] || 0;
    const selectedVariant = product.variants[selectedIdx] || product.variants[0];
    const displayImage = selectedVariant?.image || product.image;
    const displayPrice = selectedVariant?.price ?? product.rrpPrice;
    const hasVariants = product.variants.length > 1 && product.variants.some(v => v.title);
    const isLimited = getProductLimit(product.handle) !== Infinity;
    const inCart = (state.cart[selectedVariant?.id]?.quantity || 0) > 0;

    return `
      <div class="product-card" data-product-id="${product.id}" style="animation-delay: ${index * 50}ms">
        <div class="product-image" onclick="viewProductDetails('${product.id}')">
          ${displayImage ?
            `<img id="img-${product.id}" src="${displayImage}" alt="${product.title}" style="width: 100%; height: 100%; object-fit: cover;">` :
            `<div class="product-image-placeholder">
              <div class="product-initial">${product.title.charAt(0)}</div>
            </div>`
          }
          <span class="category-badge">${product.category}</span>
          ${isLimited ? `<span class="limited-badge" id="badge-${product.id}">Limited Stock</span>` : ''}
        </div>

        <div class="product-info">
          <h3 class="product-name" onclick="viewProductDetails('${product.id}')">${product.title}</h3>
          <p class="product-description">${product.shortDescription}</p>

          ${hasVariants ? (
            product.variantsAreColors
              ? `<div class="variant-swatches">
                  ${product.variants.map((v, i) => {
                    const hex = getSwatchColor(v.title) || '#c8c8c8';
                    return `<button class="variant-swatch${i === selectedIdx ? ' selected' : ''}" style="background:${hex}" onclick="selectVariant('${product.id}', ${i})" title="${v.title}"></button>`;
                  }).join('')}
                 </div>`
              : `<div class="variant-pills">
                  ${product.variants.map((v, i) => `
                    <button class="variant-pill${i === selectedIdx ? ' selected' : ''}" onclick="selectVariant('${product.id}', ${i})" title="${v.title}">${v.title}</button>
                  `).join('')}
                 </div>`
          ) : ''}

          <div class="product-footer">
            <p class="product-price">
              <span id="price-${product.id}">${formatMoney(displayPrice)}</span> <span class="price-label">RRP</span>
            </p>
            <div class="quantity-controls" id="controls-${product.id}">
              ${getQuantityHTML(product)}
            </div>
          </div>
        </div>
      </div>
    `;
  }).join('');
}

// Get quantity controls HTML
function getQuantityHTML(product) {
  const selectedIdx = state.selectedVariants[product.id] || 0;
  const variant = product.variants[selectedIdx] || product.variants[0];
  const cartKey = variant.id; // variantId — the unique cart key
  const quantity = state.cart[cartKey]?.quantity || 0;

  const minQty = getMinPerSku(product.handle);

  if (quantity > 0) {
    return `
      <div class="quantity-input-group">
        <button class="qty-btn" onclick="updateQuantity('${cartKey}', ${quantity - 1})">−</button>
        <input type="number" class="qty-display" value="${quantity}" min="1"
               onchange="updateQuantity('${cartKey}', parseInt(this.value) || 1)"
               onclick="this.select()">
        <button class="qty-btn" onclick="updateQuantity('${cartKey}', ${quantity + 1})">+</button>
      </div>
      ${quantity < minQty ? `<div class="min-qty-hint">Minimum ${minQty} per product</div>` : ''}
      <div class="item-total">${formatMoney(variant.price * quantity)}</div>
    `;
  } else {
    return `<button class="add-to-order-btn" onclick="addToCart('${product.id}')">Add to Order</button>`;
  }
}

// Add to cart
function addToCart(productId) {
  const product = state.products.find(p => p.id == productId);
  if (!product) return;

  const selectedIdx = state.selectedVariants[productId] || 0;
  const variant = product.variants[selectedIdx] || product.variants[0];
  const cartKey = variant.id; // variantId

  state.cart[cartKey] = {
    product: {
      id: cartKey,                    // cart key (= variantId) — used by sidebar controls
      productId: String(product.id),  // Shopify product ID — used by order submission
      variantId: cartKey,
      title: product.title,
      variantTitle: variant.title,
      description: product.description,
      shortDescription: product.shortDescription,
      handle: product.handle,
      image: variant.image || product.image,
      rrpPrice: variant.price,
      size: variant.title,
      category: product.category,
      isIndividualItem: product.isIndividualItem,
      sku: variant.sku,
      hsCode: variant.hsCode,
      countryOfOrigin: variant.countryOfOrigin,
    },
    quantity: Math.max(1, getMinPerSku(product.handle))
  };

  renderProductControls(productId);
  updateOrderSummary();
}

// Update quantity
// cartKey = variantId (the cart slot key). Works for both sidebar and product card controls.
function updateQuantity(cartKey, newQuantity) {
  // Capture productId before potentially deleting the cart entry
  const shopifyProductId = state.cart[cartKey]?.product?.productId || cartKey;

  // Enforce per-product order cap
  const limit = getProductLimit(state.cart[cartKey]?.product?.handle);
  if (newQuantity > limit) {
    showLimitModal(limit);
    newQuantity = limit;
  }

  if (newQuantity <= 0) {
    delete state.cart[cartKey];
    delete state.testerItemsAdded[cartKey];
  } else {
    state.cart[cartKey].quantity = newQuantity;
  }

  renderProductControls(shopifyProductId);
  updateOrderSummary();
}

// Add tester item to basket (opt-in)
function addTesterItem(productId) {
  state.testerItemsAdded[productId] = true;
  updateOrderSummary();
}

// Remove tester item from basket
function removeTesterItem(productId) {
  delete state.testerItemsAdded[productId];
  updateOrderSummary();
}

// Render product controls only (productId = Shopify product ID)
function renderProductControls(productId) {
  const product = state.products.find(p => p.id == productId);
  const container = document.getElementById(`controls-${productId}`);
  if (container && product) {
    container.innerHTML = getQuantityHTML(product);
  }

  // Show/hide the "Limited Stock" badge based on cart state
  const badge = document.getElementById(`badge-${productId}`);
  if (badge && product) {
    const idx = state.selectedVariants[productId] || 0;
    const variant = product.variants[idx] || product.variants[0];
    badge.style.display = (state.cart[variant?.id]?.quantity || 0) > 0 ? '' : 'none';
  }
}

// Select a variant swatch on a product card
function selectVariant(productId, variantIndex) {
  const product = state.products.find(p => p.id == productId);
  if (!product) return;

  state.selectedVariants[productId] = variantIndex;
  const variant = product.variants[variantIndex];

  // Update card image
  const imgEl = document.getElementById(`img-${productId}`);
  if (imgEl && variant.image) imgEl.src = variant.image;

  // Update displayed price
  const priceEl = document.getElementById(`price-${productId}`);
  if (priceEl) priceEl.textContent = formatMoney(variant.price);

  // Update swatch/pill active state
  const card = document.querySelector(`[data-product-id="${productId}"]`);
  if (card) {
    card.querySelectorAll('.variant-pill, .variant-swatch').forEach((btn, i) => {
      btn.classList.toggle('selected', i === variantIndex);
    });
  }

  // Re-render quantity controls to reflect the newly selected variant's cart state
  renderProductControls(productId);
}

// View product details (future enhancement)
function viewProductDetails(productId) {
  const product = state.products.find(p => p.id == productId);
  if (product) {
    alert(`${product.title}\n\n${product.description}\n\nPrice: $${product.rrpPrice.toFixed(2)} RRP`);
  }
}

// Calculate cart totals
function calculateTotals() {
  const items = Object.values(state.cart);
  
  // Calculate beauty bonuses (every 10 units = 1 FREE at 75% off)
  const beautyBonuses = [];
  items.forEach(item => {
    if (item.product.category?.toLowerCase() === 'beauty') {
      const bonusQty = Math.floor(item.quantity / 10);
      if (bonusQty > 0) {
        beautyBonuses.push({
          product: item.product,
          quantity: bonusQty,
          discountedPrice: item.product.rrpPrice * 0.25, // 75% off = pay 25%
          originalPrice: item.product.rrpPrice,
          savings: item.product.rrpPrice * 0.75 * bonusQty,
          enabled: !!state.testerItemsAdded[item.product.id] // Only added if user opted in
        });
      }
    }
  });
  
  // Calculate item count and subtotal (INCLUDING all items at RRP + tester items)
  let itemCount = 0;
  let subtotal = 0;
  
  items.forEach(item => {
    itemCount += item.quantity; // Regular items count
    subtotal += item.product.rrpPrice * item.quantity; // Regular items at RRP
  });
  
  // Add enabled tester items to count and subtotal
  beautyBonuses.forEach(bonus => {
    if (bonus.enabled) {
      itemCount += bonus.quantity;
      subtotal += bonus.originalPrice * bonus.quantity;
    }
  });
  
  // Calculate beauty bonus savings (only for enabled testers)
  const beautyBonusSavings = beautyBonuses
    .filter(b => b.enabled)
    .reduce((sum, bonus) => sum + bonus.savings, 0);
  const testerItemsSubtotal = beautyBonuses
    .filter(b => b.enabled)
    .reduce((sum, bonus) => sum + (bonus.originalPrice * bonus.quantity), 0);
  
  // Convert discount tiers from USD to local currency
  const localTiers = convertTiersToLocalCurrency(state.discountTiers);
  
  // Calculate subtotal for regular items only (for discount tier calculation)
  let regularItemsSubtotal = 0;
  items.forEach(item => {
    regularItemsSubtotal += item.product.rrpPrice * item.quantity;
  });
  
  // Find applicable discount tier based on regular items subtotal
  let discount = null;
  const applicableTier = localTiers.find(tier => 
    regularItemsSubtotal >= tier.minAmount && regularItemsSubtotal < tier.maxAmount
  );
  
  if (applicableTier) {
    const discountAmount = regularItemsSubtotal * (applicableTier.discountPercent / 100);
    discount = {
      percent: applicableTier.discountPercent,
      amount: discountAmount,
      label: `${applicableTier.discountPercent}% off`
    };
  }
  
  // Find next tier (if exists) - use local currency
  let nextTier = null;
  const currentTierIndex = localTiers.findIndex(tier => 
    regularItemsSubtotal >= tier.minAmount && regularItemsSubtotal < tier.maxAmount
  );
  
  if (currentTierIndex !== -1 && currentTierIndex < localTiers.length - 1) {
    const next = localTiers[currentTierIndex + 1];
    nextTier = {
      minAmount: next.minAmount,
      discountPercent: next.discountPercent,
      amountNeeded: next.minAmount - regularItemsSubtotal
    };
  }
  
  // Total = subtotal - B2B discount on regular items - tester discount on enabled tester items
  const total = subtotal - (discount ? discount.amount : 0) - beautyBonusSavings;
  
  // Paid units only — testers are unlocks, not purchases, so they don't count
  // toward the minimum order or the shipping band.
  const unitCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return { items, itemCount, unitCount, subtotal, discount, nextTier, total, beautyBonuses, beautyBonusSavings };
}

// Update order summary
function updateOrderSummary() {
  const { items, itemCount, unitCount, subtotal, discount, nextTier, total, beautyBonuses, beautyBonusSavings } = calculateTotals();

  const moq = checkMOQ();
  const ship = getShippingBand(unitCount);
  const grandTotal = total + (ship && !ship.quote ? ship.local : 0);
  
  console.log('📦 Order Summary Update:', {
    itemCount,
    subtotal: subtotal.toFixed(2),
    discountTiersAvailable: state.discountTiers.length,
    appliedDiscount: discount,
    beautyBonuses: beautyBonuses.length,
    beautyBonusSavings: beautyBonusSavings.toFixed(2),
    nextTier
  });
  
  // Update mobile indicators
  document.getElementById('mobileCartCount').textContent = itemCount;
  document.getElementById('mobileTriggerCount').textContent = itemCount;
  document.getElementById('mobileTriggerTotal').textContent = formatMoney(grandTotal);
  
  // Show/hide mobile trigger
  const mobileTrigger = document.getElementById('mobileSummaryTrigger');
  if (mobileTrigger) {
    if (window.innerWidth < 1024) {
      mobileTrigger.style.display = itemCount > 0 ? 'block' : 'none';
    } else {
      mobileTrigger.style.display = 'none';
    }
  }
  
  const summaryHTML = itemCount === 0 ? `
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <path d="M16 10a4 4 0 0 1-8 0"></path>
      </svg>
      <h3>Your order is empty</h3>
      <p>Add products to start building your order</p>
    </div>
  ` : `
    <div class="summary-header">
      <h2>Order Summary</h2>
      <span class="item-count-badge">${itemCount} ${itemCount === 1 ? 'item' : 'items'}</span>
    </div>
    
    <div class="summary-items">
      ${items.map(item => `
        <div class="summary-item">
          <div class="item-image-small">
            ${item.product.image ? 
              `<img src="${item.product.image}" alt="${item.product.title}" class="item-image-inner">` :
              `<div class="item-image-fallback">${item.product.title.charAt(0)}</div>`
            }
          </div>
          <div class="item-details">
            <div class="item-name">${item.product.title}</div>
            ${item.product.size ? `<div class="item-size">${item.product.size}</div>` : ''}
            <div class="item-controls">
              <div class="small-qty-controls">
                <button class="small-qty-btn" onclick="updateQuantity('${item.product.id}', ${item.quantity - 1})">−</button>
                <input type="number" class="small-qty-display" value="${item.quantity}" min="1" 
                       onchange="updateQuantity('${item.product.id}', parseInt(this.value) || 1)"
                       onclick="this.select()">
                <button class="small-qty-btn" onclick="updateQuantity('${item.product.id}', ${item.quantity + 1})">+</button>
              </div>
              <button class="remove-item-btn" onclick="updateQuantity('${item.product.id}', 0)">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </div>
          </div>
          <div class="item-price">${formatMoney(item.product.rrpPrice * item.quantity)}</div>
        </div>
      `).join('')}
      
      ${beautyBonuses.filter(b => b.enabled).map(bonus => `
        <div class="summary-item">
          <div class="item-image-small">
            ${bonus.product.image ? 
              `<img src="${bonus.product.image}" alt="${bonus.product.title}" class="item-image-inner">` :
              `<div class="item-image-fallback">${bonus.product.title.charAt(0)}</div>`
            }
          </div>
          <div class="item-details">
            <div class="item-name">${bonus.product.title} × ${bonus.quantity}</div>
            <div class="item-size">Tester Item - 75% Off</div>
            <div class="item-controls">
              <button class="remove-item-btn" onclick="removeTesterItem('${bonus.product.id}')">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                Remove
              </button>
            </div>
          </div>
          <div class="item-price">
            <span class="item-original-price">${formatMoney(bonus.originalPrice * bonus.quantity)}</span>
            ${formatMoney(bonus.discountedPrice * bonus.quantity)}
          </div>
        </div>
      `).join('')}
      
      ${beautyBonuses.filter(b => !b.enabled).map(bonus => `
        <div class="tester-suggestion">
          <div class="tester-suggestion__thumbnail">
            ${bonus.product.image ? 
              `<img src="${bonus.product.image}" alt="${bonus.product.title}">` :
              `<div class="tester-suggestion__thumbnail-fallback">${bonus.product.title.charAt(0)}</div>`
            }
          </div>
          <div class="tester-suggestion__info">
            <div class="tester-suggestion__label">You unlocked ${bonus.quantity} Tester${bonus.quantity > 1 ? 's' : ''}</div>
            <div class="tester-suggestion__name">${bonus.product.title} &times; ${bonus.quantity} &mdash; 75% off</div>
          </div>
          <div class="tester-suggestion__price">
            ${formatMoney(bonus.discountedPrice * bonus.quantity)}
          </div>
          <button class="tester-suggestion__btn" onclick="addTesterItem('${bonus.product.id}')">
            Add
          </button>
        </div>
      `).join('')}
    </div>
    
    <div class="discount-section">
      <div class="discount-header">
       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-percent w-4 h-4 text-primary"><line x1="19" x2="5" y1="5" y2="19"></line><circle cx="6.5" cy="6.5" r="2.5"></circle><circle cx="17.5" cy="17.5" r="2.5"></circle></svg>
        <span>Volume Discounts</span>
      </div>
      <div class="discount-tiers">
        ${convertTiersToLocalCurrency(state.discountTiers).map(tier => {
          const isActive = subtotal >= tier.minAmount && subtotal < tier.maxAmount;
          let label = '';
          if (tier.maxAmount === Infinity) {
            label = `${formatMoney(tier.minAmount)}+ → ${tier.discountPercent}% off`;
          } else if (tier.minAmount === 0) {
            label = `Under ${formatMoney(tier.maxAmount)} → ${tier.discountPercent}% off`;
          } else {
            label = `${formatMoney(tier.minAmount)}-${formatMoney(tier.maxAmount - 1)} → ${tier.discountPercent}% off`;
          }
          return `<div class="discount-tier ${isActive ? 'active' : ''}">${label}</div>`;
        }).join('')}
      </div>
      ${nextTier ? `
        <div class="next-tier-message">
          Add ${formatMoney(nextTier.amountNeeded)} more for ${nextTier.discountPercent}% off!
        </div>
      ` : ''}
    </div>
    
    <div class="summary-totals">
      <div class="total-row">
        <span class="total-label">Subtotal (RRP)</span>
        <span>${formatMoney(subtotal)}</span>
      </div>
      
      ${discount ? `
        <div class="total-row discount-row">
          <span class="total-label">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="2" x2="12" y2="22"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            Discount (${discount.label})
          </span>
          <span>-${formatMoney(discount.amount)}</span>
        </div>
      ` : ''}
      
      ${beautyBonusSavings > 0 ? `
        <div class="total-row discount-row">
          <span class="total-label">
            Tester Items Savings
          </span>
          <span>-${formatMoney(beautyBonusSavings)}</span>
        </div>
      ` : ''}
      
      ${ship ? `
        <div class="total-row">
          <span class="total-label">${ship.title}</span>
          <span>${ship.quote ? 'Quoted separately' : formatMoney(ship.local)}</span>
        </div>
      ` : ''}

      <div class="total-row final-total">
        <span class="total-label">Total</span>
        <span class="total-value">${formatMoney(grandTotal)}</span>
      </div>

      ${ship && ship.quote ? `
        <div class="savings-text">Freight for 40+ units is quoted separately — we'll add it to your invoice before you pay.</div>
      ` : ''}
      
      ${discount || beautyBonusSavings > 0 ? `
        <div class="savings-text">You save ${formatMoney((discount?.amount || 0) + beautyBonusSavings)}</div>
      ` : ''}
    </div>
    
    <div class="checkout-section">
      ${moq.ok ? '' : `
        <div class="moq-warning">
          <div class="moq-warning__title">Minimum order not met</div>
          <ul class="moq-warning__list">
            ${moq.shortBy > 0 ? `<li>Add ${moq.shortBy} more unit${moq.shortBy === 1 ? '' : 's'} — orders start at ${moq.minTotal} units.</li>` : ''}
            ${moq.shortSkus.map(sku => `<li>${sku.title}: ${sku.have} of ${sku.need} minimum per product.</li>`).join('')}
          </ul>
        </div>
      `}
      <button class="checkout-btn" onclick="proceedToCheckout()" ${!itemCount || !moq.ok ? 'disabled' : ''}>
        Proceed to Checkout
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </button>
      <div class="ordering-as">
        Ordering as ${state.customerEmail || 'Guest'}
      </div>
    </div>
  `;
  
  // Update both desktop and mobile summaries
  document.getElementById('orderSummary').innerHTML = summaryHTML;
  const mobileOrderSummary = document.getElementById('mobileOrderSummary');
  if (mobileOrderSummary) {
    mobileOrderSummary.innerHTML = summaryHTML;
  }
}

// Customer display name, most reliable source first. The body tag is the
// primary source; fall back to the address and then the email so the review
// page never renders "undefined".
function getCustomerName(el) {
  const fromTag = (el?.dataset.customerName || '').trim();
  if (fromTag) return fromTag;
  const a = state.customerAddress;
  const fromAddress = [a?.first_name, a?.last_name].filter(Boolean).join(' ').trim()
    || (a?.name || '').trim();
  return fromAddress || state.customerEmail || 'Customer';
}

// Proceed to checkout
async function proceedToCheckout() {
  const { itemCount, total, subtotal, discount } = calculateTotals();
  
  if (itemCount === 0) {
    alert('Your cart is empty');
    return;
  }

  const moq = checkMOQ();
  if (!moq.ok) {
    const reasons = [
      moq.shortBy > 0 ? `• Add ${moq.shortBy} more unit${moq.shortBy === 1 ? '' : 's'} (minimum ${moq.minTotal} units per order)` : '',
      ...moq.shortSkus.map(sku => `• ${sku.title}: ${sku.have} of ${sku.need} minimum per product`)
    ].filter(Boolean).join('\n');
    alert('Minimum order not met:\n\n' + reasons);
    return;
  }
  
  if (!state.customerEmail) {
    alert('Please log in to complete your order');
    // Store current page, then redirect to login
    sessionStorage.setItem('login_redirect', window.location.pathname);
    window.location.href = '/account/login';
    return;
  }
  
  // Route to appropriate checkout flow based on customer tags
  if (state.checkoutFlow === 'shopify') {
    // NEW: Shopify Checkout Flow (B2B tag customers)
    await proceedToShopifyCheckout();
    return;
  }
  
  // EXISTING: Pay Later Flow (B2B_paylater tag customers)
  await proceedToPayLaterCheckout();
}

// NEW: Shopify Checkout Flow - Create draft order and show review page
async function proceedToShopifyCheckout() {
  const { itemCount, unitCount, total, subtotal, discount, beautyBonuses, beautyBonusSavings } = calculateTotals();
  const ship = getShippingBand(unitCount);
  
  const customerData = document.querySelector('[data-customer-id]');
  const customerId = customerData?.dataset.customerId;
  const customerName = getCustomerName(customerData);
  
  if (!customerId) {
    alert('Customer information not found. Please refresh and try again.');
    return;
  }
  
  const checkoutBtn = document.querySelector('.checkout-btn');
  const originalText = checkoutBtn.innerHTML;
  
  try {
    console.log('🛒 Creating B2B draft order for Shopify checkout...');
    console.log('📦 Cart total:', total);
    console.log('📧 Customer:', state.customerEmail);
    console.log('💰 Volume discount:', discount ? discount.percent + '%' : 'None');
    
    // Show loading state
    checkoutBtn.disabled = true;
    checkoutBtn.innerHTML = 'Creating Order...';
    
    // Prepare cart items with category info for beauty bonus calculation
    // Always send price in store base currency (GBP) so Shopify draft order
    // prices are correct.
    // rrpPrice = shopify_gbp_price * exchangeRate, so dividing restores GBP.
    const cartItems = Object.values(state.cart).map(item => ({
      variant_id: parseInt(item.product.variantId),
      product_id: parseInt(item.product.productId),
      quantity: item.quantity,
      title: item.product.title,
      handle: item.product.handle,
      price: item.product.rrpPrice / (state.exchangeRate || 1),
      category: item.product.category
    }));
    
    // Prepare customer address (if available)
    let shippingAddress = null;
    let billingAddress = null;
    if (state.customerAddress) {
      shippingAddress = {
        first_name: state.customerAddress.first_name || state.customerAddress.name?.split(' ')[0],
        last_name: state.customerAddress.last_name || state.customerAddress.name?.split(' ').slice(1).join(' '),
        address1: state.customerAddress.address1,
        address2: state.customerAddress.address2 || '',
        city: state.customerAddress.city,
        province: state.customerAddress.province,
        country: state.customerAddress.country,
        country_code: state.customerAddress.country_code || '',
        zip: state.customerAddress.zip,
        phone: state.customerAddress.phone || ''
      };
      billingAddress = shippingAddress; // Use same for now
    }
    
    // Call B2B Shopify checkout function to create draft order
    const response = await fetch(CONFIG.SHOPIFY_CHECKOUT_URL, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'apikey': CONFIG.SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        customer_id: parseInt(customerId),
        customer_email: state.customerEmail,
        cart_items: cartItems,
        discount_tiers: state.discountTiers, // In GBP
        shipping_address: shippingAddress,
        billing_address: billingAddress,
        opted_in_tester_product_ids: Object.keys(state.testerItemsAdded).map(Number)
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Draft order creation failed:', errorData);
      throw new Error(errorData.error || 'Failed to create order');
    }
    
    const result = await response.json();
    console.log('✅ Draft order created:', result);
    
    if (!result.success || !result.order) {
      throw new Error('Invalid response from server - no order data');
    }
    
    // Extract draft order ID
    let draftOrderId = result.order.id;
    if (typeof draftOrderId === 'string' && draftOrderId.includes('gid://')) {
      draftOrderId = draftOrderId.split('/').pop();
    }
    
    console.log('🎫 Draft order:', result.order.name);
    console.log('💰 Volume discount:', result.volume_discount_percent + '%');
    console.log('🎁 Beauty bonuses:', result.beauty_bonuses);
    console.log('🔗 Invoice URL:', result.invoice_url);
    
    // Store order data for review page
    state.lastOrder = {
      orderNumber: result.order.name.replace('#', ''),
      orderName: result.order.name,
      draftOrderId: draftOrderId,
      customerName: customerName,
      items: Object.values(state.cart),
      subtotal: subtotal,
      discount: discount,
      total: total,
      isDraftOrder: true,
      invoiceUrl: result.invoice_url, // Store invoice URL for payment
      isB2BCheckout: true // Flag to show "Proceed to Payment" instead of edit options
    };
    
    // Show review page (confirmation page but with payment button)
    showB2BReviewPage(result.order, customerName, {
      subtotal: subtotal,
      discount: discount,
      total: total,
      ship: ship,
      items: Object.values(state.cart),
      invoiceUrl: result.invoice_url,
      beautyBonuses: beautyBonuses,
      beautyBonusSavings: beautyBonusSavings
    });
    
    // Clear cart
    state.cart = {};
    updateOrderSummary();
    
  } catch (error) {
    console.error('💥 Order creation error:', error);
    alert('There was an error creating your order: ' + error.message + '\n\nPlease try again or contact support.');
    
    // Reset button
    if (checkoutBtn) {
      checkoutBtn.disabled = false;
      checkoutBtn.innerHTML = originalText;
    }
  }
}

// EXISTING: Pay Later Checkout Flow - Create draft order and show confirmation page
async function proceedToPayLaterCheckout() {
  const { itemCount, unitCount, total, subtotal, discount, beautyBonuses, beautyBonusSavings } = calculateTotals();
  const ship = getShippingBand(unitCount);
  
  // Get customer ID from body data attribute
  const customerData = document.querySelector('[data-customer-id]');
  const customerId = customerData?.dataset.customerId;
  const customerName = getCustomerName(customerData);
  
  if (!customerId) {
    alert('Customer information not found. Please refresh and try again.');
    return;
  }
  
  // Get discount percent (or 0 if no discount)
  const discountPercent = discount ? discount.percent : 0;
  
  const checkoutBtn = document.querySelector('.checkout-btn');
  const originalText = checkoutBtn.innerHTML;
  
  try {
    console.log('🛒 Creating draft order...');
    console.log('📦 Cart total:', total);
    console.log('📧 Customer:', state.customerEmail);
    console.log('💰 Discount:', discountPercent + '%');
    
    // Show loading state
    checkoutBtn.disabled = true;
    checkoutBtn.innerHTML = 'Creating Order...';
    
    // Prepare line items with category info for beauty bonus calculation
    // Send price in store base currency (GBP) so draft order prices are correct.
    const lineItems = Object.values(state.cart).map(item => ({
      variant_id: parseInt(item.product.variantId),
      product_id: parseInt(item.product.productId),
      quantity: item.quantity,
      title: item.product.title,
      handle: item.product.handle,
      price: item.product.rrpPrice / (state.exchangeRate || 1),
      category: item.product.category
    }));
    
    // Prepare customer address (if available)
    let shippingAddress = null;
    if (state.customerAddress) {
      shippingAddress = {
        first_name: state.customerAddress.first_name || state.customerAddress.name?.split(' ')[0],
        last_name: state.customerAddress.last_name || state.customerAddress.name?.split(' ').slice(1).join(' '),
        address1: state.customerAddress.address1,
        address2: state.customerAddress.address2 || '',
        city: state.customerAddress.city,
        province: state.customerAddress.province,
        country: state.customerAddress.country,
        country_code: state.customerAddress.country_code || '',
        zip: state.customerAddress.zip,
        phone: state.customerAddress.phone || ''
      };
    }
    
    // Determine if this is an update to existing order or new order
    let response;
    if (state.isAmendingOrder && state.lastOrder?.draftOrderId) {
      // Update existing draft order
      console.log('🔄 Updating draft order:', state.lastOrder.draftOrderId);
      response = await fetch(CONFIG.UPDATE_DRAFT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft_order_id: state.lastOrder.draftOrderId,
          line_items: lineItems,
          discount_percent: discountPercent,
          subtotal: subtotal,
          shipping_address: shippingAddress,
          billing_address: shippingAddress,
          opted_in_tester_product_ids: Object.keys(state.testerItemsAdded).map(Number)
        })
      });
    } else {
      // Create new draft order
      console.log('✨ Creating new draft order');
      response = await fetch(CONFIG.SUPABASE_FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_email: state.customerEmail,
          customer_id: parseInt(customerId),
          line_items: lineItems,
          discount_percent: discountPercent,
          subtotal: subtotal,
          shipping_address: shippingAddress,
          billing_address: shippingAddress,
          opted_in_tester_product_ids: Object.keys(state.testerItemsAdded).map(Number)
        })
      });
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Draft order creation failed:', errorData);
      throw new Error(errorData.error || 'Failed to create order');
    }
    
    const result = await response.json();
    console.log('✅ Draft order created:', result);
    
    if (!result.success || !result.order) {
      throw new Error('Invalid response from server');
    }
    
    // Extract draft order ID
    let draftOrderId = result.order.id;
    if (typeof draftOrderId === 'string' && draftOrderId.includes('gid://')) {
      draftOrderId = draftOrderId.split('/').pop();
    }
    
    // Store original order items BEFORE updating state.lastOrder (for amendment comparison)
    const originalOrderItems = state.isAmendingOrder && state.lastOrder ? state.lastOrder.items : null;
    const originalSubtotal = state.isAmendingOrder && state.lastOrder ? state.lastOrder.subtotal : 0;
    const originalTotal = state.isAmendingOrder && state.lastOrder ? state.lastOrder.total : 0;
    const originalDiscount = state.isAmendingOrder && state.lastOrder ? state.lastOrder.discount : null;
    
    // Store order data before clearing cart
    state.lastOrder = {
      orderNumber: result.order.name.replace('#', ''),
      orderName: result.order.name,
      draftOrderId: draftOrderId,
      customerName: customerName,
      items: Object.values(state.cart),
      subtotal: subtotal,
      discount: discount,
      total: total,
      isDraftOrder: true // All orders created through portal are draft orders
    };
    
    // Store submission timestamp ONLY if this is a new order (not an amendment)
    if (!state.isAmendingOrder) {
      state.orderSubmittedAt = new Date().toISOString();
    }
    // If amending, keep the original submission timestamp so timer continues
    
    // Send appropriate Slack notification
    if (state.isAmendingOrder && originalOrderItems) {
      // Send amendment notification with comparison
      sendAmendmentNotification(
        result.order, 
        customerData, 
        discount, 
        state.amendingOrderRef,
        originalOrderItems,
        originalSubtotal,
        originalTotal,
        originalDiscount
      ).catch(err => {
        console.warn('⚠️ Amendment notification failed:', err);
      });
      console.log('✏️ Order updated:', state.amendingOrderRef, '- Draft order ID:', draftOrderId);
    } else {
      // Send regular new order notification
      sendSlackNotification(result.order, customerData, discount).catch(err => {
        console.warn('⚠️ Slack notification failed:', err);
      });
    }
    
    // Reset amendment flags
    state.isAmendingOrder = false;
    state.amendingOrderRef = null;
    
    // Show confirmation page with our calculated totals (already in customer currency)
    showConfirmationPage(result.order, customerName, {
      subtotal: subtotal,
      discount: discount,
      total: total,
      ship: ship,
      items: Object.values(state.cart),
      beautyBonuses: beautyBonuses,
      beautyBonusSavings: beautyBonusSavings
    });
    
    // Clear cart
    state.cart = {};
    updateOrderSummary();
    
  } catch (error) {
    console.error('💥 Order creation error:', error);
    alert('There was an error creating your order: ' + error.message + '\n\nPlease try again or contact support.');
    
    // Reset button
    if (checkoutBtn) {
      checkoutBtn.disabled = false;
      checkoutBtn.innerHTML = originalText;
    }
  }
}

// Send Slack notification for new order
async function sendSlackNotification(order, customerDataset, discount) {
  try {
    console.log('📨 Sending Slack notification...');
    
    // Extract numeric ID from GraphQL GID if needed
    let numericId = order.id;
    if (typeof order.id === 'string' && order.id.includes('gid://')) {
      numericId = order.id.split('/').pop();
    }
    
    // Calculate beauty bonuses for Slack notification
    const beautyBonuses = [];
    state.lastOrder.items.forEach(item => {
      if (item.product.category?.toLowerCase() === 'beauty') {
        const bonusQty = Math.floor(item.quantity / 10);
        if (bonusQty > 0) {
          beautyBonuses.push({
            product: item.product,
            quantity: bonusQty,
            discountedPrice: item.product.rrpPrice * 0.25,
            originalPrice: item.product.rrpPrice
          });
        }
      }
    });
    
    // Calculate subtotal (ALL items at RRP)
    let correctSubtotal = 0;
    state.lastOrder.items.forEach(item => {
      correctSubtotal += item.product.rrpPrice * item.quantity;
    });
    
    // Prepare order data with shipping, duties, taxes
    const orderData = {
      invoiceNumber: order.name.replace('#', ''),
      subtotal: correctSubtotal, // All items at RRP
      total: state.lastOrder.total,
      currency: state.currency,
      shippingCost: 0, // Will be updated by backend from draft order
      duties: 0, // Will be updated by backend from draft order
      taxes: 0, // Will be updated by backend from draft order
      draftOrderUrl: `https://admin.shopify.com/store/sachi-skin/draft_orders/${numericId}`,
      logoUrl: 'https://cdn.shopify.com/s/files/1/0236/1258/8078/files/logo-min.png?v=1629423611'
    };
    
    const customerData = {
      name: customerDataset.dataset.customerName,
      email: state.customerEmail,
      company: state.customerAddress?.company || '',
      shippingAddress: state.customerAddress,
      billingAddress: state.customerAddress // Use same for now, backend will get from draft order
    };
    
    // Build cart items including both regular and tester items
    const cartItems = [];
    state.lastOrder.items.forEach(item => {
      const bonus = beautyBonuses.find(b => b.product.id === item.product.id);
      const regularQty = bonus ? item.quantity - bonus.quantity : item.quantity;
      
      // Add regular items
      if (regularQty > 0) {
        cartItems.push({
          title: item.product.title,
          size: item.product.size || 'Standard',
          quantity: regularQty,
          price: item.product.rrpPrice,
          total: item.product.rrpPrice * regularQty,
          sku: item.product.sku,
          hsCode: item.product.hsCode,
          category: item.product.category,
          countryOfOrigin: item.product.countryOfOrigin
        });
      }
      
      // Add tester items
      if (bonus) {
        cartItems.push({
          title: `${item.product.title} (Tester - 75% Off)`,
          size: item.product.size || 'Standard',
          quantity: bonus.quantity,
          price: bonus.discountedPrice,
          total: bonus.discountedPrice * bonus.quantity,
          sku: item.product.sku,
          hsCode: item.product.hsCode,
          category: item.product.category,
          countryOfOrigin: item.product.countryOfOrigin,
          isTesterItem: true
        });
      }
    });
    
    const discountInfo = {
      amount: discount ? discount.amount : 0,
      percentage: discount ? discount.percent : 0
    };
    
    const response = await fetch(CONFIG.SLACK_NOTIFY_URL, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'apikey': CONFIG.SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${CONFIG.SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        orderData,
        customerData,
        cartItems,
        discountInfo
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send Slack notification');
    }
    
    const result = await response.json();
    console.log('✅ Slack notification sent:', result);
    
  } catch (error) {
    console.error('❌ Slack notification error:', error);
    throw error;
  }
}

function showConfirmationPage(order, customerName, totals) {
  // Close mobile summary drawer if open
  const overlay = document.getElementById('mobileSummaryOverlay');
  const drawer = document.getElementById('mobileSummaryDrawer');
  if (overlay && drawer) {
    overlay.classList.remove('active');
    drawer.classList.remove('active');
    document.body.style.overflow = '';
  }
  
  // Hide main content
  document.querySelector('.main-content').style.display = 'none';
  document.querySelector('header').style.display = 'none';
  
  // Show confirmation page
  const confirmationPage = document.getElementById('confirmationPage');
  confirmationPage.style.display = 'block';
  
  // Update page title for B2B_paylater
  const titleElement = document.querySelector('.confirmation-title');
  if (titleElement) {
    titleElement.textContent = 'Order Submitted Successfully';
  }
  
  // Update reference text for B2B_paylater
  const referenceElement = document.querySelector('.confirmation-reference');
  if (referenceElement) {
    referenceElement.innerHTML = `Order Reference: <strong id="orderReference"></strong>`;
  }
  
  // Show the "Thank you" section for B2B_paylater customers
  const nextStepsSection = document.querySelector('.confirmation-next-steps');
  if (nextStepsSection) {
    nextStepsSection.style.display = 'block';
  }
  
  // Set order reference with amendment indicator if applicable
  const orderRefElement = document.getElementById('orderReference');
  if (state.lastOrder.isAmendment) {
    orderRefElement.innerHTML = `${order.name} <span style="color: #f59e0b; font-size: 0.875rem;">(Amendment of ${state.lastOrder.originalOrderRef})</span>`;
  } else {
    orderRefElement.textContent = order.name;
  }
  
  // Use the totals we already calculated in customer's currency
  // Don't try to convert draft order prices - they're in shop currency
  const { subtotal, discount, total, ship, items, beautyBonuses, beautyBonusSavings } = totals;
  
  // Build order details HTML
  const detailsHtml = `
    <div class="confirmation-customer-info">
      <p class="confirmation-customer-label">Customer</p>
      <p class="confirmation-customer-name">${customerName}</p>
    </div>
    
    <div class="confirmation-items-section">
      <p class="confirmation-items-heading">Order Items</p>
      ${items.map(item => `
        <div class="confirmation-item-row">
          <div>
            <div class="confirmation-item-title">${item.product.title}</div>
            <div class="confirmation-item-qty">Qty: ${item.quantity}</div>
          </div>
          <div class="confirmation-item-price">${formatMoney(item.product.rrpPrice * item.quantity)}</div>
        </div>
      `).join('')}
      
      ${beautyBonuses && beautyBonuses.filter(b => b.enabled).length > 0 ? beautyBonuses.filter(b => b.enabled).map(bonus => `
        <div class="confirmation-item-row">
          <div>
            <div class="confirmation-item-title">${bonus.product.title} × ${bonus.quantity}</div>
            <div class="confirmation-item-qty">Tester Item - 75% Off</div>
          </div>
          <div class="confirmation-item-price">
            <span style="text-decoration: line-through; color: var(--muted-foreground); font-size: 0.75rem; display: block;">${formatMoney(bonus.originalPrice * bonus.quantity)}</span>
            ${formatMoney(bonus.discountedPrice * bonus.quantity)}
          </div>
        </div>
      `).join('') : ''}
    </div>
    
    <div class="confirmation-totals">
      <div class="confirmation-total-row">
        <span>Subtotal</span>
        <span>${formatMoney(subtotal)}</span>
      </div>
      ${discount ? `
      <div class="confirmation-total-row confirmation-discount-row">
        <span>Discount (${discount.percent}%)</span>
        <span>-${formatMoney(discount.amount)}</span>
      </div>
      ` : ''}
      ${beautyBonusSavings > 0 ? `
      <div class="confirmation-total-row confirmation-discount-row">
        <span>Tester Items Savings</span>
        <span>-${formatMoney(beautyBonusSavings)}</span>
      </div>
      ` : ''}
      ${ship ? `
      <div class="confirmation-total-row">
        <span>${ship.title}</span>
        <span>${ship.quote ? 'Quoted separately' : formatMoney(ship.local)}</span>
      </div>
      ` : ''}
      <div class="confirmation-total-row confirmation-final-total">
        <span>Total</span>
        <span>${formatMoney(total + (ship && !ship.quote ? ship.local : 0))}</span>
      </div>
    </div>
  `;
  
  document.getElementById('confirmationOrderDetails').innerHTML = detailsHtml;
  
  // Show appropriate button based on order type and time elapsed
  // Always hide timer — no edit window restriction for Pay Later orders
  const timerContainer = document.getElementById('editTimerContainer');
  if (timerContainer) timerContainer.style.display = 'none';

  // Inject action buttons — Pay Later info on top, then buttons below
  const buttonContainer = document.querySelector('.confirmation-actions');
  if (buttonContainer) {
    buttonContainer.innerHTML = `
      <div style="display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1.25rem; border-radius: 0.375rem; font-size: 2rem; font-weight: 500; vertical-align: middle; padding-bottom: 24px">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0;">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        Order set as Pay Later - invoice will follow via email
      </div>
      <div style="display: flex; gap: 1rem; flex-wrap: wrap;justify-content:center;">
        <button class="reorder-btn" onclick="editLastOrder()"
                style="display: inline-flex; align-items: center; background: var(--sachi-earthy-orange);">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               style="margin-right: 0.5rem; flex-shrink: 0;">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          Edit This Order
        </button>
        <button onclick="window.location.reload()">Place Another Order</button>
      </div>
    `;
  }

  window.scrollTo(0, 0);
}

// NEW: Show B2B Review Page (before payment)
function showB2BReviewPage(order, customerName, totals) {
  // Close mobile summary drawer if open
  const overlay = document.getElementById('mobileSummaryOverlay');
  const drawer = document.getElementById('mobileSummaryDrawer');
  if (overlay && drawer) {
    overlay.classList.remove('active');
    drawer.classList.remove('active');
    document.body.style.overflow = '';
  }
  
  // Hide main content
  document.querySelector('.main-content').style.display = 'none';
  document.querySelector('header').style.display = 'none';
  
  // Show confirmation page
  const confirmationPage = document.getElementById('confirmationPage');
  confirmationPage.style.display = 'block';
  
  // Update page title for B2B review
  const titleElement = document.querySelector('.confirmation-title');
  if (titleElement) {
    titleElement.textContent = 'Review Order';
  }
  
  // Update reference text
  const referenceElement = document.querySelector('.confirmation-reference');
  if (referenceElement) {
    referenceElement.innerHTML = `Order: <strong id="orderReference">${order.name}</strong> - <span style="color: #78716c;">Review before payment</span>`;
  }
  
  // Hide the "Thank you" section for B2B customers
  const nextStepsSection = document.querySelector('.confirmation-next-steps');
  if (nextStepsSection) {
    nextStepsSection.style.display = 'none';
  }
  
  const { subtotal, discount, total, ship, items, invoiceUrl, beautyBonuses, beautyBonusSavings } = totals;
  
  // Build order details HTML
  const detailsHtml = `
    <div class="confirmation-customer-info">
      <p class="confirmation-customer-label">Customer</p>
      <p class="confirmation-customer-name">${customerName}</p>
      <p class="confirmation-customer-label" style="margin-top: 0.5rem;">Review your order before proceeding to payment</p>
    </div>
    
    <div class="confirmation-items-section">
      <p class="confirmation-items-heading">Order Items</p>
      ${items.map(item => `
        <div class="confirmation-item-row">
          <div>
            <div class="confirmation-item-title">${item.product.title}</div>
            <div class="confirmation-item-qty">Qty: ${item.quantity}</div>
          </div>
          <div class="confirmation-item-price">${formatMoney(item.product.rrpPrice * item.quantity)}</div>
        </div>
      `).join('')}
      
      ${beautyBonuses && beautyBonuses.filter(b => b.enabled).length > 0 ? beautyBonuses.filter(b => b.enabled).map(bonus => `
        <div class="confirmation-item-row">
          <div>
            <div class="confirmation-item-title">${bonus.product.title} × ${bonus.quantity}</div>
            <div class="confirmation-item-qty">Tester Item - 75% Off</div>
          </div>
          <div class="confirmation-item-price">
            <span style="text-decoration: line-through; color: var(--muted-foreground); font-size: 0.75rem; display: block;">${formatMoney(bonus.originalPrice * bonus.quantity)}</span>
            ${formatMoney(bonus.discountedPrice * bonus.quantity)}
          </div>
        </div>
      `).join('') : ''}
    </div>
    
    <div class="confirmation-totals">
      <div class="confirmation-total-row">
        <span>Subtotal</span>
        <span>${formatMoney(subtotal)}</span>
      </div>
      ${discount ? `
      <div class="confirmation-total-row confirmation-discount-row">
        <span>Discount (${discount.percent}%)</span>
        <span>-${formatMoney(discount.amount)}</span>
      </div>
      ` : ''}
      ${beautyBonusSavings > 0 ? `
      <div class="confirmation-total-row confirmation-discount-row">
        <span>Tester Items Savings</span>
        <span>-${formatMoney(beautyBonusSavings)}</span>
      </div>
      ` : ''}
      ${ship ? `
      <div class="confirmation-total-row">
        <span>${ship.title}</span>
        <span>${ship.quote ? 'Quoted separately' : formatMoney(ship.local)}</span>
      </div>
      ` : ''}
      <div class="confirmation-total-row confirmation-final-total">
        <span>Total</span>
        <span>${formatMoney(total + (ship && !ship.quote ? ship.local : 0))}</span>
      </div>
    </div>
  `;
  
  document.getElementById('confirmationOrderDetails').innerHTML = detailsHtml;
  
  // Hide edit/reorder buttons, show payment button instead
  const editBtn = document.getElementById('editOrderBtn');
  const reorderBtn = document.getElementById('reorderBtn');
  const timerContainer = document.getElementById('editTimerContainer');
  
  if (editBtn) editBtn.style.display = 'none';
  if (reorderBtn) reorderBtn.style.display = 'none';
  if (timerContainer) timerContainer.style.display = 'none';
  
  // Add "Edit Order" and "Proceed to Payment" buttons
  const buttonContainer = editBtn?.parentElement || document.querySelector('.confirmation-actions');
  if (buttonContainer) {
    buttonContainer.innerHTML = `
      <button class="reorder-btn" onclick="editB2BOrder()" style="display: inline-flex;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
        </svg>
        Edit Order
      </button>
      <button class="edit-order-btn" onclick="proceedToPayment()" style="display: inline-flex; background: #00838F;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="2" y="5" width="20" height="14" rx="2"></rect>
          <line x1="2" y1="10" x2="22" y2="10"></line>
        </svg>
        Proceed to Payment
      </button>
    `;
  }
  
  // Scroll to top
  window.scrollTo(0, 0);
}

// NEW: Edit B2B order - go back to cart
function editB2BOrder() {
  if (!state.lastOrder) {
    console.warn('⚠️ No order to edit');
    return;
  }
  
  console.log('✏️ Editing B2B order:', state.lastOrder.orderName);
  
  // Restore cart from last order
  state.cart = {};
  state.lastOrder.items.forEach(item => {
    state.cart[item.product.id] = {
      product: item.product,
      quantity: item.quantity
    };
  });
  
  // Return to main page
  document.getElementById('confirmationPage').style.display = 'none';
  document.querySelector('.main-content').style.display = 'block';
  document.querySelector('header').style.display = 'block';
  
  // Update UI
  updateOrderSummary();
  renderProducts();
  
  // Scroll to top
  window.scrollTo(0, 0);
}

// NEW: Proceed to Shopify payment
function proceedToPayment() {
  if (!state.lastOrder || !state.lastOrder.invoiceUrl) {
    alert('Payment link not found. Please try again.');
    return;
  }
  
  console.log('💳 Redirecting to Shopify checkout:', state.lastOrder.invoiceUrl);
  
  // Redirect to Shopify checkout (invoice URL)
  window.location.href = state.lastOrder.invoiceUrl;
}

// Show the "Limited Stock" order-cap modal
function showLimitModal(limit) {
  const modal = document.getElementById('limitModal');
  if (!modal) return;
  const maxEl = document.getElementById('limitModalMax');
  if (maxEl) maxEl.textContent = limit;
  modal.classList.add('active');
}

function closeLimitModal() {
  document.getElementById('limitModal')?.classList.remove('active');
}

// Clean up countdown interval when leaving page
window.addEventListener('beforeunload', () => {
  if (state.editCountdownInterval) {
    clearInterval(state.editCountdownInterval);
  }
});

// Expose functions globally
window.addToCart = addToCart;
window.updateQuantity = updateQuantity;
window.selectVariant = selectVariant;
window.viewProductDetails = viewProductDetails;
window.proceedToCheckout = proceedToCheckout;
window.editLastOrder = editLastOrder;
window.reorderLastOrder = reorderLastOrder;
window.viewLastOrder = viewLastOrder;
window.editB2BOrder = editB2BOrder;
window.proceedToPayment = proceedToPayment;
window.closeLimitModal = closeLimitModal;
window.toggleMobileSummary = function() {
  const overlay = document.getElementById('mobileSummaryOverlay');
  const drawer = document.getElementById('mobileSummaryDrawer');
  if (overlay && drawer) {
    overlay.classList.toggle('active');
    drawer.classList.toggle('active');
    document.body.style.overflow = drawer.classList.contains('active') ? 'hidden' : '';
  }
};
